import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Text,
  Spinner,
  VStack,
  Badge,
  Button,
  Select,
  HStack,
  Switch,
  Flex,
  Image,
  useColorModeValue,
  IconButton,
} from '@chakra-ui/react';
import { ArrowBackIcon } from '@chakra-ui/icons';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Card from 'components/card/Card';

export default function UserStoriesPage() {
  const { userId } = useParams();
  const navigate = useNavigate();

  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);
  const [showDeleted, setShowDeleted] = useState(false);

  const baseUrl = process.env.REACT_APP_BASE_URL?.replace(/\/$/, '');
  const token = localStorage.getItem('token');

  const bgCard = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.200');

  const headers = useMemo(
    () => ({
      Authorization: `Bearer ${token}`,
    }),
    [token],
  );
  const visibleStories = useMemo(
    () => (showDeleted ? stories : stories.filter((s) => !s.isDeleted)),
    [stories, showDeleted],
  );
  useEffect(() => {
    fetchStories(showDeleted);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDeleted]);

  const fetchStories = async (includeDeleted = showDeleted) => {
    try {
      const res = await axios.get(
        `${baseUrl}/api/admin/user-stories/by-user/${userId}`,
        { headers, params: { includeDeleted } },
      );
      if (res.data?.success) setStories(res.data.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (storyId, newStatus) => {
    setUpdatingId(storyId);

    try {
      await axios.patch(
        `${baseUrl}/api/admin/user-stories/${storyId}/status`,
        { status: newStatus },
        { headers },
      );

      setStories((prev) =>
        prev.map((s) => (s._id === storyId ? { ...s, status: newStatus } : s)),
      );
    } catch (err) {
      console.error(err);
    } finally {
      setUpdatingId(null);
    }
  };

  const togglePopular = async (storyId) => {
    try {
      const res = await axios.patch(
        `${baseUrl}/api/admin/user-stories/${storyId}/popular`,
        {},
        { headers },
      );

      setStories((prev) =>
        prev.map((s) =>
          s._id === storyId ? { ...s, isPopular: res.data.isPopular } : s,
        ),
      );
    } catch (err) {
      console.error(err);
    }
  };

  const toggleRecommended = async (storyId) => {
    try {
      const res = await axios.patch(
        `${baseUrl}/api/admin/user-stories/${storyId}/recommended`,
        {},
        { headers },
      );

      setStories((prev) =>
        prev.map((s) =>
          s._id === storyId
            ? { ...s, isRecommended: res.data.isRecommended }
            : s,
        ),
      );
    } catch (err) {
      console.error(err);
    }
  };
  const deleteStory = async (storyId) => {
    if (!window.confirm('Move this story to trash? It can be restored later.'))
      return;
    try {
      await axios.delete(`${baseUrl}/api/admin/stories/${storyId}`, {
        headers,
      });
      setStories((prev) =>
        prev.map((s) => (s._id === storyId ? { ...s, isDeleted: true } : s)),
      );
    } catch (err) {
      console.error(err);
    }
  };

  const restoreStory = async (storyId) => {
    try {
      await axios.patch(
        `${baseUrl}/api/admin/stories/${storyId}/restore`,
        {},
        { headers },
      );
      setStories((prev) =>
        prev.map((s) => (s._id === storyId ? { ...s, isDeleted: false } : s)),
      );
    } catch (err) {
      console.error(err);
    }
  };

  const getStatusColor = (status) => {
    if (status === 'approved') return 'green';
    if (status === 'rejected') return 'red';
    return 'yellow';
  };

  if (loading)
    return (
      <Flex mt="100px" justify="center">
        <Spinner size="xl" thickness="4px" color="blue.500" />
      </Flex>
    );
  const thStyle = {
    padding: '12px',
    textAlign: 'left',
    borderBottom: '2px solid #ddd',
    fontWeight: '600',
  };

  const tdStyle = {
    padding: '12px',
  };
  return (
    <Card p="30px" mt="80px">
      {/* HEADER */}
      <Flex justify="space-between" align="center" mb={8}>
        <HStack spacing={4}>
          <IconButton
            icon={<ArrowBackIcon />}
            aria-label="Back"
            onClick={() => navigate(-1)}
            variant="outline"
          />
          <Text fontSize="24px" fontWeight="800">
            User Stories
          </Text>
        </HStack>

        <HStack spacing={4}>
          <Badge colorScheme="blue" px={4} py={2} borderRadius="full">
            {visibleStories.length} Total
          </Badge>
          <HStack spacing={2}>
            <Text fontSize="sm">Show Deleted</Text>
            <Switch
              colorScheme="red"
              isChecked={showDeleted}
              onChange={() => setShowDeleted((p) => !p)}
            />
          </HStack>
        </HStack>
      </Flex>

      {visibleStories.length === 0 ? (
        <Flex justify="center" align="center" minH="200px">
          <Text color="gray.500">No stories found</Text>
        </Flex>
      ) : (
        <Box overflowX="auto">
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              tableLayout: 'fixed', // ✅ column alignment fix
            }}
          >
            <thead>
              <tr style={{ background: '#f5f5f5' }}>
                <th style={thStyle}>Category</th>
                <th style={thStyle}>Title</th>
                <th style={thStyle}>Description</th>
                <th style={thStyle}>Cover</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Popular</th>
                <th style={thStyle}>Recommended</th>
                <th style={thStyle}>Update</th>
                <th style={thStyle}>Chapters</th>
                <th style={thStyle}>Actions</th>
              </tr>
            </thead>

            <tbody>
              {visibleStories.map((story) => (
                <tr key={story._id} style={{ borderBottom: '1px solid #ddd' }}>
                  {/* CATEGORY */}
                  <td style={tdStyle}>
                    <Badge colorScheme="purple">
                      {story.category?.name || 'N/A'}
                    </Badge>
                  </td>
                  {/* TITLE */}
                  <td style={tdStyle}>{story.title}</td>
                  <td style={tdStyle}>{story.description}</td>

                  {/* COVER */}
                  <td style={tdStyle}>
                    {story.coverImage ? (
                      <Image
                        src={`${baseUrl}${story.coverImage}`}
                        boxSize="80px"
                        objectFit="cover"
                        borderRadius="md"
                      />
                    ) : (
                      'No Image'
                    )}
                  </td>

                  {/* STATUS BADGE */}
                  <td style={tdStyle}>
                    <Badge colorScheme={getStatusColor(story.status)}>
                      {story.status.toUpperCase()}
                    </Badge>
                  </td>
                  {/* POPULAR */}
                  <td style={tdStyle}>
                    <Switch
                      colorScheme="blue"
                      isChecked={story.isPopular}
                      onChange={() => togglePopular(story._id)}
                    />
                  </td>

                  {/* RECOMMENDED */}
                  <td style={tdStyle}>
                    <Switch
                      colorScheme="green"
                      isChecked={story.isRecommended}
                      onChange={() => toggleRecommended(story._id)}
                    />
                  </td>
                  {/* UPDATE STATUS */}
                  <td style={tdStyle}>
                    <Select
                      size="sm"
                      value={story.status}
                      isDisabled={updatingId === story._id}
                      onChange={(e) => updateStatus(story._id, e.target.value)}
                    >
                      <option value="pending">Pending</option>
                      <option value="approved">Approved</option>
                      <option value="rejected">Rejected</option>
                    </Select>
                  </td>

                  <td style={tdStyle}>
                    <Flex
                      align="center"
                      gap={2}
                      flexWrap="wrap" // ✅ small screen pe break ho jayega
                    >
                      <Badge
                        colorScheme="purple"
                        borderRadius="full"
                        px={3}
                        py={1}
                        minW="50px" // ✅ fixed width for alignment
                        textAlign="center"
                      >
                        📚 {story.chapters?.length || 0}
                      </Badge>

                      {story.chapters && story.chapters.length > 0 && (
                        <Button
                          size="sm"
                          colorScheme="blue"
                          variant="solid"
                          minW="120px" // ✅ button size consistent
                          justifyContent="center"
                          onClick={() =>
                            navigate(`/admin/story/${story._id}/chapters`)
                          }
                        >
                          View Chapters
                        </Button>
                      )}
                    </Flex>
                  </td>

                  <td style={tdStyle}>
                    {story.isDeleted ? (
                      <Button
                        size="sm"
                        colorScheme="green"
                        onClick={() => restoreStory(story._id)}
                      >
                        Restore
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        colorScheme="red"
                        onClick={() => deleteStory(story._id)}
                      >
                        Delete
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
      )}
    </Card>
  );
}
